import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  AdminEventRsvpDto,
  AdminPermission,
  AdminVenueEventCommentListItemDto,
  AdminVenueEventDto,
  AdminVenueEventLikeListItemDto,
  AdminVisitPlanListItemDto,
  AppFeature,
  EventSource,
} from '@ivy-monorepo/shared'
import Table from '../../components/Table'
import Pagination from '../../components/Pagination'
import ConfirmationModal from '../../components/modals/ConfirmationModal'
import VenueEventCommentModal from '../../components/modals/VenueEventCommentModal'
import EventJourneyUserModal from '../../components/modals/EventJourneyUserModal'
import EventDetailModal from '../../components/modals/EventDetailModal'
import { adminService } from '../../services/admin.service'
import { useSearch } from '../../contexts/SearchContext'
import { usePermissions } from '../../contexts/PermissionsContext'
import { withCalendarStatusLabel } from '../../utils/googleCalendarStatus'
import { formatTicketPrice } from '../../utils/ticketPrice'
import { EVENT_SOURCE_LABELS, formatEventSource, formatEventStatus } from '../../utils/eventJourney'

type EventsView = 'events' | 'rsvps' | 'visit-plans' | 'comments' | 'event-likes'

const VIEW_TABS: Array<{ key: EventsView; label: string; permission: AdminPermission; feature?: AppFeature }> = [
  { key: 'events', label: 'Events', permission: AdminPermission.Events },
  { key: 'rsvps', label: 'RSVPs', permission: AdminPermission.Events },
  { key: 'visit-plans', label: 'Visit plans', permission: AdminPermission.Events },
  { key: 'comments', label: 'Comments', permission: AdminPermission.Events },
  { key: 'event-likes', label: 'Event likes', permission: AdminPermission.Events },
]

function calculateOptimalPageSize(): number {
  const viewportHeight = window.innerHeight
  const rowHeight = 56
  const headerHeight = 220
  const paginationHeight = 60
  const availableHeight = viewportHeight - headerHeight - paginationHeight
  const calculatedPageSize = Math.floor(availableHeight / rowHeight)
  return Math.max(10, Math.min(100, calculatedPageSize))
}

function defaultSortBy(view: EventsView): string {
  switch (view) {
    case 'events':
      // Newest-created first, so an event just added is at the top.
      return 'createdAt'
    case 'rsvps':
      return 'rsvpedAt'
    case 'visit-plans':
      return 'plannedDate'
    case 'comments':
    case 'event-likes':
      return 'createdAt'
    default:
      return 'createdAt'
  }
}

const commentColumns = [
  { label: '', accessor: 'author.imageUrl', type: 'userAvatar' },
  { label: 'Author', accessor: 'author.lastName', type: 'fullNameAndTelephone' },
  { label: 'Comment', accessor: 'content', type: 'markdown' },
  { label: 'Event', accessor: 'event.name', type: 'text' as any },
  { label: '', accessor: 'venue.logoUrl', type: 'venueAvatar' },
  { label: 'Venue', accessor: 'venue.name', type: 'nameAndAddress', className: 'width-30p' },
  { label: 'Likes', accessor: 'likeCount', type: 'number' },
  { label: 'Created', accessor: 'createdAt', type: 'datetime' },
]

const VenueEventsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { searchValue } = useSearch()
  const { hasPermission, isLoading: isPermissionsLoading, features } = usePermissions()

  const [events, setEvents] = useState<AdminVenueEventDto[]>([])
  const [eventsPagination, setEventsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [rsvps, setRsvps] = useState<AdminEventRsvpDto[]>([])
  const [rsvpsPagination, setRsvpsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [visitPlans, setVisitPlans] = useState<AdminVisitPlanListItemDto[]>([])
  const [visitPlansPagination, setVisitPlansPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [comments, setComments] = useState<AdminVenueEventCommentListItemDto[]>([])
  const [commentsPagination, setCommentsPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [eventLikes, setEventLikes] = useState<AdminVenueEventLikeListItemDto[]>([])
  const [eventLikesPagination, setEventLikesPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [selectedCommentPublicId, setSelectedCommentPublicId] = useState<string | null>(null)
  const [deleteEventLikeTarget, setDeleteEventLikeTarget] = useState<number | null>(null)
  const [journeyRsvp, setJourneyRsvp] = useState<AdminEventRsvpDto | null>(null)
  const [selectedEvent, setSelectedEvent] = useState<AdminVenueEventDto | null>(null)
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue)

  const visibleTabs = useMemo(
    () =>
      isPermissionsLoading
        ? []
        : VIEW_TABS.filter(tab => hasPermission(tab.permission) && (!tab.feature || features[tab.feature])),
    [features, hasPermission, isPermissionsLoading],
  )

  const requestedView = (searchParams.get('view') as EventsView | null) ?? 'events'
  const activeView = visibleTabs.some(tab => tab.key === requestedView)
    ? requestedView
    : (visibleTabs[0]?.key ?? 'events')

  const currentPage = parseInt(searchParams.get('page') || '1')
  const urlPageSize = searchParams.get('pageSize')
  const currentPageSize = urlPageSize ? parseInt(urlPageSize) : calculateOptimalPageSize()
  const currentSortBy = searchParams.get('sortBy') || defaultSortBy(activeView)
  const currentSortOrder = (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc'
  const activeOnly = searchParams.get('activeOnly') !== 'false'
  const rsvpSourceFilter = searchParams.get('source') || ''

  const refreshComments = useCallback(async () => {
    const response = await adminService.getEventComments(
      currentPage,
      currentPageSize,
      debouncedSearchValue,
      currentSortBy,
      currentSortOrder,
    )
    setComments(response.comments)
    setCommentsPagination(response.pagination)
  }, [currentPage, currentPageSize, currentSortBy, currentSortOrder, debouncedSearchValue])

  const refreshEventLikes = useCallback(async () => {
    const response = await adminService.getEventLikes(
      currentPage,
      currentPageSize,
      debouncedSearchValue,
      currentSortBy,
      currentSortOrder,
    )
    setEventLikes(response.likes)
    setEventLikesPagination(response.pagination)
  }, [currentPage, currentPageSize, currentSortBy, currentSortOrder, debouncedSearchValue])

  useEffect(() => {
    if (requestedView !== activeView && visibleTabs.length > 0) {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.set('view', activeView)
      nextParams.set('page', '1')
      setSearchParams(nextParams, { replace: true })
    }
  }, [activeView, requestedView, searchParams, setSearchParams, visibleTabs.length])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchValue(searchValue), 300)
    return () => clearTimeout(timer)
  }, [searchValue])

  useEffect(() => {
    if (activeView !== 'events') {
      return
    }

    const fetchEvents = async () => {
      try {
        const response = await adminService.getVenueEvents(
          currentPage,
          currentPageSize,
          debouncedSearchValue,
          currentSortBy,
          currentSortOrder,
          activeOnly,
        )
        setEvents(
          response.events.map(event => ({
            ...event,
            ticketPriceLabel: formatTicketPrice(event.ticketPriceCents),
            statusLabel: formatEventStatus(event.isActive, event.visibility),
          })),
        )
        setEventsPagination(response.pagination)
      } catch (err) {
        console.log('Failed to fetch venue events', err)
      }
    }

    fetchEvents()
  }, [activeOnly, activeView, currentPage, currentPageSize, currentSortBy, currentSortOrder, debouncedSearchValue])

  useEffect(() => {
    if (activeView !== 'rsvps') {
      return
    }

    const fetchRsvps = async () => {
      try {
        const response = await adminService.getEventRsvps(
          currentPage,
          currentPageSize,
          debouncedSearchValue,
          currentSortBy,
          currentSortOrder,
          undefined,
          { source: rsvpSourceFilter || undefined },
        )
        setRsvps(response.rsvps.map(rsvp => ({ ...rsvp, sourceLabel: formatEventSource(rsvp.source) })))
        setRsvpsPagination(response.pagination)
      } catch (err) {
        console.log('Failed to fetch event RSVPs', err)
      }
    }

    fetchRsvps()
  }, [activeView, currentPage, currentPageSize, currentSortBy, currentSortOrder, debouncedSearchValue, rsvpSourceFilter])

  const refreshVisitPlans = useCallback(async () => {
    try {
      const response = await adminService.getVisitPlans(
        currentPage,
        currentPageSize,
        debouncedSearchValue,
        currentSortBy,
        currentSortOrder,
      )
      setVisitPlans(response.visitPlans.map(withCalendarStatusLabel))
      setVisitPlansPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch visit plans', err)
    }
  }, [currentPage, currentPageSize, currentSortBy, currentSortOrder, debouncedSearchValue])

  const handleSyncVisitPlanToGoogleCalendar = useCallback(
    async (row: AdminVisitPlanListItemDto) => {
      if (!row.googleCalendarConnected) return
      await adminService.syncVisitPlanToGoogleCalendar(row.publicId)
      refreshVisitPlans()
    },
    [refreshVisitPlans],
  )

  useEffect(() => {
    if (activeView !== 'visit-plans') {
      return
    }

    refreshVisitPlans()
  }, [activeView, refreshVisitPlans])

  useEffect(() => {
    if (activeView !== 'comments') {
      return
    }

    refreshComments().catch(err => console.log('Failed to fetch event comments', err))
  }, [activeView, refreshComments])

  useEffect(() => {
    if (activeView !== 'event-likes') {
      return
    }

    refreshEventLikes().catch(err => console.log('Failed to fetch event likes', err))
  }, [activeView, refreshEventLikes])

  const handleViewChange = (view: EventsView) => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('view', view)
    nextParams.set('page', '1')
    nextParams.set('sortBy', defaultSortBy(view))
    setSearchParams(nextParams)
  }

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('page', page.toString())
    navigate(`?${newParams.toString()}`)
  }

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('sortBy', sortBy)
    newParams.set('sortOrder', sortOrder)
    newParams.set('page', '1')
    navigate(`?${newParams.toString()}`)
  }

  const handleActiveOnlyChange = (checked: boolean) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('activeOnly', checked.toString())
    newParams.set('page', '1')
    navigate(`?${newParams.toString()}`)
  }

  const handleRsvpSourceChange = (source: string) => {
    const newParams = new URLSearchParams(searchParams)
    if (source) {
      newParams.set('source', source)
    } else {
      newParams.delete('source')
    }
    newParams.set('page', '1')
    navigate(`?${newParams.toString()}`)
  }

  const handleCommentDeleted = () => {
    setSelectedCommentPublicId(null)

    const isLastItemOnPage = comments.length === 1
    if (isLastItemOnPage && commentsPagination.page > 1) {
      handlePageChange(commentsPagination.page - 1)
      return
    }

    refreshComments().catch(err => console.log('Failed to refresh event comments', err))
  }

  const paginationProps =
    activeView === 'events'
      ? { currentPage: eventsPagination.page, totalPages: eventsPagination.totalPages }
      : activeView === 'rsvps'
        ? { currentPage: rsvpsPagination.page, totalPages: rsvpsPagination.totalPages }
        : activeView === 'visit-plans'
          ? { currentPage: visitPlansPagination.page, totalPages: visitPlansPagination.totalPages }
          : activeView === 'comments'
            ? { currentPage: commentsPagination.page, totalPages: commentsPagination.totalPages }
            : { currentPage: eventLikesPagination.page, totalPages: eventLikesPagination.totalPages }

  const showPagination = paginationProps.totalPages > 1

  return (
    <div className="users-container">
      <div className="users-header">
        <h2>Venue events</h2>
        {showPagination && (
          <Pagination
            currentPage={paginationProps.currentPage}
            totalPages={paginationProps.totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <div className="tab-bar" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          {visibleTabs.map(tab => {
            const classNames = ['tab']
            if (tab.key === activeView) {
              classNames.push('active')
            }

            return (
              <button
                key={tab.key}
                type="button"
                className={classNames.join(' ')}
                onClick={() => handleViewChange(tab.key)}
              >
                {tab.label}
              </button>
            )
          })}
        </div>

        {activeView === 'rsvps' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            Source
            <select value={rsvpSourceFilter} onChange={e => handleRsvpSourceChange(e.target.value)}>
              <option value="">All</option>
              {Object.values(EventSource).map(source => (
                <option key={source} value={source}>
                  {EVENT_SOURCE_LABELS[source]}
                </option>
              ))}
              <option value="none">Not reported</option>
            </select>
          </label>
        )}

        {activeView === 'events' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={e => handleActiveOnlyChange(e.target.checked)}
            />
            Active only
          </label>
        )}
      </div>

      <div className="users-table-container">
        <div className="table-wrapper">

          {activeView === 'events' && (
            <Table
              id="venue-events"
              name="Events"
              keyField="publicId"
              initialSortColumn={currentSortBy}
              initialSortOrder={currentSortOrder}
              columns={[
                { label: 'Flyer', accessor: 'flyerUrl', type: 'thumbnail' },
                { label: 'Event', accessor: 'name', type: 'eventName' },
                { label: '', accessor: 'venue.logoUrl', type: 'venueAvatar' },
                { label: 'Venue', accessor: 'venue.name', type: 'nameAndAddress', className: 'width-30p' },
                { label: 'Starts', accessor: 'eventStartDate', type: 'datetime' },
                { label: 'Status', accessor: 'statusLabel', type: 'text' as any },
                { label: 'RSVPs', accessor: 'rsvpedCount', type: 'number' },
              ]}
              sourceData={events}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={(row: AdminVenueEventDto) => setSelectedEvent(row)}
              onSortChange={handleSortChange}
              disableGlobalSearch={false}
              disableClientSort={true}
              options={[
                {
                  label: 'View journey',
                  onClick: (row: AdminVenueEventDto) => navigate(`/admin/venue-events/${row.publicId}/journey`),
                },
                {
                  label: 'Open venue',
                  onClick: (row: AdminVenueEventDto) => navigate(`/admin/venues/${row.venue.publicId}`),
                },
              ]}
            />
          )}

          {activeView === 'rsvps' && (
            <Table
              id="event-rsvps"
              name="Event RSVPs"
              keyField="id"
              initialSortColumn={currentSortBy}
              initialSortOrder={currentSortOrder}
              columns={[
                { label: '', accessor: 'user.imageUrl', type: 'userAvatar' },
                { label: 'User', accessor: 'user.lastName', type: 'fullNameAndTelephone' },
                { label: 'Event', accessor: 'event.name', type: 'text' as any },
                { label: '', accessor: 'venue.logoUrl', type: 'venueAvatar' },
                { label: 'Venue', accessor: 'venue.name', type: 'nameAndAddress', className: 'width-30p' },
                { label: 'Source', accessor: 'sourceLabel', type: 'text' as any },
                { label: 'RSVPed at', accessor: 'rsvpedAt', type: 'datetime' },
              ]}
              sourceData={rsvps}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={(row: AdminEventRsvpDto) => setJourneyRsvp(row)}
              onSortChange={handleSortChange}
              disableGlobalSearch={false}
              disableClientSort={true}
            />
          )}

          {activeView === 'visit-plans' && (
            <Table
              id="visit-plans"
              name="Visit plans"
              keyField="publicId"
              initialSortColumn={currentSortBy}
              initialSortOrder={currentSortOrder}
              columns={[
                { label: '', accessor: 'user.imageUrl', type: 'userAvatar' },
                { label: 'User', accessor: 'user.lastName', type: 'fullNameAndTelephone' },
                { label: '', accessor: 'venue.logoUrl', type: 'venueAvatar' },
                { label: 'Venue', accessor: 'venue.name', type: 'nameAndAddress', className: 'width-30p' },
                { label: 'Planned date', accessor: 'plannedDate', type: 'date' },
                { label: 'Time slot', accessor: 'timeSlot', type: 'enum', enumName: 'VenueVisitPlanTimeSlot' },
                { label: 'Active', accessor: 'isActive', type: 'text' as any },
                { label: 'Passed', accessor: 'isPassed', type: 'text' as any },
                { label: 'Created', accessor: 'createdAt', type: 'datetime' },
                { label: 'Calendar', accessor: 'calendarStatusLabel', type: 'text' as any },
              ]}
              sourceData={visitPlans}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={() => {}}
              onSortChange={handleSortChange}
              disableGlobalSearch={false}
              disableClientSort={true}
              options={[
                {
                  label: 'Sync to Google Calendar',
                  onClick: handleSyncVisitPlanToGoogleCalendar,
                },
              ]}
            />
          )}

          {activeView === 'comments' && (
            <Table
              id="event-comments"
              name="Event comments"
              keyField="publicId"
              initialSortColumn={currentSortBy}
              initialSortOrder={currentSortOrder}
              columns={commentColumns}
              sourceData={comments}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={(row: AdminVenueEventCommentListItemDto) => setSelectedCommentPublicId(row.publicId)}
              onSortChange={handleSortChange}
              disableGlobalSearch={false}
              disableClientSort={true}
            />
          )}

          {activeView === 'event-likes' && (
            <Table
              id="event-likes"
              name="Event likes"
              keyField="id"
              initialSortColumn={currentSortBy}
              initialSortOrder={currentSortOrder}
              columns={[
                { label: '', accessor: 'user.imageUrl', type: 'userAvatar' },
                { label: 'User', accessor: 'user.lastName', type: 'fullNameAndTelephone' },
                { label: 'Event', accessor: 'event.name', type: 'text' as any },
                { label: '', accessor: 'venue.logoUrl', type: 'venueAvatar' },
                { label: 'Venue', accessor: 'venue.name', type: 'nameAndAddress', className: 'width-30p' },
                { label: 'Liked at', accessor: 'createdAt', type: 'datetime' },
              ]}
              sourceData={eventLikes}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={() => {}}
              onDelete={(row: AdminVenueEventLikeListItemDto) => setDeleteEventLikeTarget(row.id)}
              onSortChange={handleSortChange}
              disableGlobalSearch={false}
              disableClientSort={true}
            />
          )}
        </div>
      </div>

      <VenueEventCommentModal
        isOpen={!!selectedCommentPublicId}
        onClose={() => setSelectedCommentPublicId(null)}
        publicId={selectedCommentPublicId}
        onChanged={() => refreshComments().catch(err => console.log('Failed to refresh event comments', err))}
        onDeleted={handleCommentDeleted}
      />

      <EventDetailModal isOpen={selectedEvent !== null} onClose={() => setSelectedEvent(null)} event={selectedEvent} />

      <EventJourneyUserModal
        isOpen={journeyRsvp !== null}
        onClose={() => setJourneyRsvp(null)}
        eventPublicId={journeyRsvp?.event.publicId ?? null}
        eventName={journeyRsvp?.event.name}
        userPublicId={journeyRsvp?.user.publicId ?? null}
      />

      <ConfirmationModal
        isOpen={deleteEventLikeTarget !== null}
        onClose={() => setDeleteEventLikeTarget(null)}
        onConfirm={async () => {
          if (deleteEventLikeTarget === null) {
            return false
          }

          try {
            await adminService.deleteEventLike(deleteEventLikeTarget)
            await refreshEventLikes()
          } catch (err) {
            console.log('Failed to delete event like', err)
          } finally {
            setDeleteEventLikeTarget(null)
          }

          return true
        }}
        title="Remove event like?"
      >
        <p>Are you sure you want to remove this like?</p>
      </ConfirmationModal>
    </div>
  )
}

export default VenueEventsPage
